<template>
  <h-action-dialog
    :model-value="showProfileModal"
    :title="modalTitle"
    max-width="800"
    :eager="modalWasShown"
    :aria-hidden="!showProfileModal"
    hide-save
    :hide-close="isCloseBlocked"
    :close-text="useHTranslate('citizen_close')"
    class="profile-modal--elevated"
    @update:model-value="handleDialogUpdate"
  >
    <!-- Info alert explaining the purpose -->
    <v-alert v-if="showContactEditor" color="info" variant="tonal" class="mb-4 text-body-2">
      {{ useHTranslate('citizen_profile_modal_info') }}
    </v-alert>

    <!-- Force verification warning -->
    <v-alert v-if="isCloseBlocked" color="warning" variant="tonal" class="mb-4 text-body-2">
      <v-icon class="mr-2">mdi:mdi-lock</v-icon>
      {{ useHTranslate('citizen_profile_modal_verification_required') }}
    </v-alert>

    <!-- Shared contact editor (includes verify button + all dialogs) -->
    <profile-contact-preference v-if="showPreferenceDecision" elevated @changed="handlePreferenceChanged" />

    <div v-show="showContactEditor">
      <profile-contact-editor
        ref="contactEditorRef"
        :show-card="false"
        :read-only="!showContactEditor"
        :suggested-values="suggestedValues"
        :required-fields="requiredAttributes"
        elevated
      />
    </div>

    <!-- Custom footer when close is blocked -->
    <template v-if="isCloseBlocked" #footer>
      <v-row class="ma-0 justify-end">
        <v-col cols="auto" class="pa-0">
          <h-action-button type="neutral" size="default" disabled>
            {{ useHTranslate('citizen_profile_modal_confirm') }}
          </h-action-button>
        </v-col>
      </v-row>
    </template>
  </h-action-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useHTranslate, useOCommon, useOSettings } from '@onify/helix-core';
import { useProfileModal } from '@/onify/citizen/composables/useProfileModal.js';
import { useContactDetailsPreference } from '@/onify/citizen/composables/useContactDetailsPreference.js';
import ProfileContactEditor from '@/onify/citizen/components/profile-contact-editor.vue';
import ProfileContactPreference from '@/onify/citizen/components/profile-contact-preference.vue';

// Profile modal composable (handles query params internally)
const { showProfileModal, suggestedValues, closeProfileModal } = useProfileModal();

const { oUser } = useOCommon();
const { citizen_my_profile } = useOSettings();
const { featureEnabled, status: preferenceStatus, isOptedIn, isOptedOut } = useContactDetailsPreference();

const contactEditorRef = ref(null);
const showPreferenceDecision = computed(() => featureEnabled.value && preferenceStatus.value === 'unknown');
const showContactEditor = computed(() => !featureEnabled.value || isOptedIn.value);
const modalTitle = computed(() =>
  showPreferenceDecision.value ? useHTranslate('citizen_contact_preference_title') : useHTranslate('citizen_my_profile_contact_details'),
);

// Force verification setting
const forceVerification = computed(() => citizen_my_profile?.value?.force_verification_in_profile_modal === true);

// Close modal if verified values match query setting
const closeIfVerifiedMatch = computed(() => citizen_my_profile?.value?.close_modal_if_verified_match_query === true);

// Required attributes that must be verified
const requiredAttributes = computed(() => {
  const settingValue = citizen_my_profile?.value?.required_contact_detail_attributes;
  if (!settingValue) return [];
  return settingValue.split(',').map((field) => field.trim());
});

/**
 * Determines if the modal can be skipped because all required attributes
 * are already verified and query values (if provided) match existing data.
 */
const shouldSkipModal = computed(() => {
  if (!showContactEditor.value) return false;
  if (!closeIfVerifiedMatch.value) return false;
  if (requiredAttributes.value.length === 0) return false;

  const contactDetails = oUser.value?.custom?.citizen?.profile_information?.contact_details || {};

  return requiredAttributes.value.every((key) => {
    const data = contactDetails[key];
    // Must have data, a value, and be verified
    if (!data || !data.value || !data.verifiedAt) return false;

    // If a query param was provided for this key, it must match the stored value
    const queryValue = suggestedValues.value[key];
    if (queryValue && queryValue !== data.value) return false;

    return true;
  });
});

// Keep opened modal contents mounted so closing it cannot abort an active verification.
// A modal that has never been shown still renders lazily.
const modalWasShown = ref(false);

// Watch: auto-close modal if all required attributes are already verified and match query
// Only runs before the modal is presented to the user (initial load check)
watch(
  [showProfileModal, shouldSkipModal, isOptedOut],
  ([isOpen, shouldSkip, optedOut]) => {
    if (isOpen && featureEnabled.value && optedOut) {
      closeProfileModal();
      return;
    }
    if (isOpen && shouldSkip && !modalWasShown.value) {
      closeProfileModal();
    }
    if (isOpen && !shouldSkip) {
      modalWasShown.value = true;
    }
  },
  { immediate: true },
);

// Check if all required attributes are verified
const isCloseBlocked = computed(() => {
  if (!showContactEditor.value) return false;
  if (!forceVerification.value || !showProfileModal.value) return false;
  if (requiredAttributes.value.length === 0) return false;

  const contactDetails = oUser.value?.custom?.citizen?.profile_information?.contact_details || {};

  return requiredAttributes.value.some((key) => {
    const data = contactDetails[key];
    // Block if: no data, no value, or not verified
    if (!data || !data.value || !data.verifiedAt) return true;
    return false;
  });
});

const handlePreferenceChanged = (status) => {
  if (status === 'opted_out') {
    closeProfileModal();
  } else if (status === 'opted_in') {
    modalWasShown.value = true;
  }
};

// Handle dialog close - block if verification is required
const handleDialogUpdate = (value) => {
  if (!value) {
    if (isCloseBlocked.value) return; // Prevent closing
    closeProfileModal();
  }
};
</script>

<style>
/* Ensure profile modal stays on top of other dialogs (e.g., case modal) */
.profile-modal--elevated.v-overlay {
  z-index: 2500 !important;
}
/* Fallback: target via parent class if attrs don't propagate to overlay root */
.v-overlay:has(.profile-modal--elevated) {
  z-index: 2500 !important;
}
</style>
